import apiClient from './config';
import { handleApiError } from './utils';
import { Product, ProductSection, GiftShop } from '../types/product';
import { getCurrentLanguage } from '../config/languageConfig';
import { createCacheKey, getCacheData, setCacheData } from './cacheUtils';

/**
 * 기프트샵 정보 가져오기
 * @param shopId 기프트샵 ID
 * @returns 기프트샵 정보
 */
export const getGiftShopInfo = async (shopId: string): Promise<GiftShop | null> => {
  try {
    const endpoint = `/api/v1/stores/${shopId}`;
    const currentLang = getCurrentLanguage();
    
    // 캐시 키 생성
    const cacheKey = createCacheKey(endpoint, {});
    
    // 캐시된 데이터 확인
    const cachedData = getCacheData<GiftShop>(cacheKey, currentLang);
    if (cachedData) {
      return cachedData;
    }
    
    const response = await apiClient.get(endpoint);
    
    if (response.data) {
      const giftShop: GiftShop = response.data;
      
      // 변환된 데이터 캐싱
      setCacheData(cacheKey, giftShop, currentLang);
      
      return giftShop;
    }
    
    return null;
  } catch (error) {
    console.error('기프트샵 정보를 가져오는데 실패했습니다:', error);
    return null;
  }
};

/**
 * 기프트샵 상품 목록 가져오기
 * @param shopId 기프트샵 ID (예: 'gift-shop-1')
 * @returns 상품 섹션 목록
 */
export const getGiftShopProducts = async (shopId: string): Promise<ProductSection[]> => {
  try {
    const endpoint = `/api/v1/stores/gift-shop/products-simple`;
    const currentLang = getCurrentLanguage();
    
    // 캐시 키 생성
    const cacheKey = createCacheKey(endpoint, {});
    
    // 캐시된 데이터 확인
    const cachedData = getCacheData<ProductSection[]>(cacheKey, currentLang);
    if (cachedData) {
      return cachedData;
    }
    
    const response = await apiClient.get(endpoint);
    
    if (response.data && Array.isArray(response.data)) {
      // API 응답을 ProductSection 구조로 변환
      const productSections = transformProductsToSections(response.data);
      
      // 변환된 데이터 캐싱
      setCacheData(cacheKey, productSections, currentLang);
      
      return productSections;
    }
    
    return [];
  } catch (error) {
    console.error('기프트샵 상품을 가져오는데 실패했습니다:', error);
    return [];
  }
};

// 섹션 정렬 순서 정의 (우선순위가 높을수록 앞에 배치)
const SECTION_SORT_ORDER: Record<string, number> = {
  // 음식 카테고리
  'Tourist Souvenir': 1,
  'Korean Roasted Seaweed': 2,
  'Korean Coffee & Tea': 3,
  'Red Ginseng': 4,
  'Korean Snacks': 5,
  'Instant Noodles (Pack)': 6,
  'Cup Noodles': 7,
  
  
  // 뷰티 카테고리
  'Facial Mask': 8,
  'Toner': 9,
  'Lotion/Serum/Essence': 10,
  'Sun Care': 11,
  
  // 전자제품
  'Portable Charger': 12,
};

/**
 * 섹션을 사용자 정의 순서로 정렬
 * @param sections 정렬할 섹션 배열
 * @returns 정렬된 섹션 배열
 */
const sortSectionsByCustomOrder = (sections: ProductSection[]): ProductSection[] => {
  return [...sections].sort((a, b) => {
    const orderA = SECTION_SORT_ORDER[a.name] || 999; // 정의되지 않은 섹션은 맨 뒤로
    const orderB = SECTION_SORT_ORDER[b.name] || 999;
    
    if (orderA === orderB) {
      // 우선순위가 같으면 알파벳 순으로 정렬
      return a.name.localeCompare(b.name);
    }
    
    return orderA - orderB;
  });
};

/**
 * Product 배열을 ProductSection 배열로 변환
 * @param products Product 배열
 * @returns ProductSection 배열
 */
const transformProductsToSections = (products: Product[]): ProductSection[] => {
  // section별로 그룹화
  const sectionMap = new Map<string, Product[]>();
  
  products.forEach(product => {
    const sectionName = product.section || 'Other';
    if (!sectionMap.has(sectionName)) {
      sectionMap.set(sectionName, []);
    }
    sectionMap.get(sectionName)!.push(product);
  });
  
  // ProductSection 배열로 변환
  const sections: ProductSection[] = [];
  let index = 0;
  
  sectionMap.forEach((products, sectionName) => {
    // 섹션 내 상품들을 displayOrder 기준으로 정렬
    const sortedProducts = products.sort((a, b) => {
      // displayOrder가 있는 경우 우선 정렬
      if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
        return a.displayOrder - b.displayOrder;
      }
      
      // displayOrder가 없는 상품은 뒤로 보내고, 이름순으로 정렬
      if (a.displayOrder !== undefined && b.displayOrder === undefined) {
        return -1;
      }
      if (a.displayOrder === undefined && b.displayOrder !== undefined) {
        return 1;
      }
      
      // 둘 다 displayOrder가 없으면 이름순으로 정렬 (영어 이름 기준)
      return (a.productNameEn || '').localeCompare(b.productNameEn || '');
    });
    
    sections.push({
      id: `section-${index}`,
      name: sectionName,
      products: sortedProducts
    });
    index++;
  });
  
  // 섹션 정렬 적용
  return sortSectionsByCustomOrder(sections);
};

/**
 * 개별 상품 상세 정보 조회
 * @param productId 상품 ID
 * @returns 상품 상세 정보
 */
export const getProductDetail = async (productId: string): Promise<Product> => {
  try {
    const response = await apiClient.get<Product>(`/api/v1/stores/gift-shop/products/${productId}`);
    return response.data;
  } catch (error) {
    console.error(`[getProductDetail] 상품 상세 정보 조회 실패 (productId: ${productId}):`, error);
    const apiError = handleApiError(error);
    throw new Error(apiError.message || '상품 상세 정보를 불러오는데 실패했습니다.');
  }
}; 